import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { MDBBtn, MDBRow, MDBCol, MDBIcon, MDBCard, MDBCardHeader, MDBCardBody } from 'mdb-react-ui-kit'
import CarrierMgmtStore from './../../CarrierMgmt.store'
import { UIInput, UISelect } from './../../../../components/forms/form-fields'
import {
  STATE_CODES,
  STATE_NAMES,
  formatStateCount,
} from './../../../ProductAvailability/ProductAvailability.constants'

import './CarrierProductsForm.scss'

const sortProducts = (a, b) =>
  (parseInt(a.sort_order) || 0) - (parseInt(b.sort_order) || 0) || `${a.name}`.localeCompare(`${b.name}`)

const toForm = product => ({
  id: product?.id || null,
  carrier_id: product?.carrier_id || '',
  name: product?.name || '',
  short_name: product?.short_name || '',
  footnote: product?.footnote || '',
  sort_order: product?.sort_order ?? 0,
  states: Object.fromEntries((product?.states || []).map(s => [s.state, s.note || ''])),
})

// carrierId: scope to one carrier (carrier editor).  Omit to manage every carrier's products.
const CarrierProductsForm = ({ carrierId }) => {
  const [form, setForm] = useState(null)
  const [error, setError] = useState(null)
  const isScoped = parseInt(carrierId) > 0

  useEffect(() => {
    setForm(null)
    setError(null)
    if (carrierId !== 'new') CarrierMgmtStore.fetchProducts(isScoped ? carrierId : null)
  }, [carrierId, isScoped])

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  const toggleState = code =>
    setForm(prev => {
      const states = { ...prev.states }
      if (states.hasOwnProperty(code)) delete states[code]
      else states[code] = ''
      return { ...prev, states }
    })

  const setAllStates = checked =>
    setForm(prev => ({
      ...prev,
      states: checked ? Object.fromEntries(STATE_CODES.map(code => [code, prev.states[code] || ''])) : {},
    }))

  const setNote = (code, note) => setForm(prev => ({ ...prev, states: { ...prev.states, [code]: note } }))

  const startEdit = product => {
    setError(null)
    setForm(toForm(product || (isScoped ? { carrier_id: parseInt(carrierId) } : null)))
  }

  const onSubmit = async event => {
    event.preventDefault()

    const missing = []
    if (!parseInt(form.carrier_id)) missing.push('Carrier')
    if (!`${form.name}`.trim()) missing.push('Product Name')
    if (missing.length) return setError(`The following fields are required: ${missing.join(' and ')}.`)

    setError(null)
    try {
      await CarrierMgmtStore.saveProduct({
        id: form.id,
        carrier_id: parseInt(form.carrier_id),
        name: `${form.name}`.trim(),
        short_name: `${form.short_name}`.trim(),
        footnote: `${form.footnote}`.trim(),
        sort_order: parseInt(form.sort_order) || 0,
        states: Object.keys(form.states).map(state => ({ state, note: form.states[state] })),
      })
      setForm(null)
    } catch (ex) {
      setError(ex?.message || 'Failed to save the product.  Please try again.')
    }
  }

  const onDelete = async product => {
    if (!window.confirm(`Delete "${product.name}"? It will be removed from the Product Availability map.`)) return

    setError(null)
    try {
      await CarrierMgmtStore.deleteProduct(product.id)
    } catch (ex) {
      setError(ex?.message || 'Failed to delete the product.  Please try again.')
    }
  }

  const renderLockOut = () => {
    if (isScoped || !carrierId) return null
    return <div className='lockout-window'>lockout</div>
  }

  const renderError = () =>
    error ? (
      <div className='product-error' role='alert'>
        {error}
      </div>
    ) : null

  const renderForm = () => {
    const selected = Object.keys(form.states).sort(),
      carriers = CarrierMgmtStore.getCarriers()
        .map(c => ({ text: `${c.c_name}`.trim(), value: `${c.id}` }))
        .sort((a, b) => a.text.localeCompare(b.text))

    return (
      <form noValidate onSubmit={onSubmit} className='product-form'>
        {renderError()}
        <MDBRow>
          {isScoped ? null : (
            <MDBCol size='12'>
              <UISelect
                label='Carrier'
                name='carrier_id'
                search
                options={carriers}
                value={form.carrier_id ? `${form.carrier_id}` : ''}
                onChange={e => set('carrier_id', e.target.value)}
              />
            </MDBCol>
          )}
          <MDBCol size='12' sm='8'>
            <UIInput
              label='Product Name'
              type='text'
              name='name'
              value={form.name}
              onChange={e => set('name', e.target.value)}
              autoComplete='off'
            />
          </MDBCol>
          <MDBCol size='12' sm='4'>
            <UIInput
              label='Short Label'
              type='text'
              name='short_name'
              value={form.short_name}
              onChange={e => set('short_name', e.target.value)}
              autoComplete='off'
            />
          </MDBCol>
          <MDBCol size='12' sm='8'>
            <UIInput
              label='Footnote'
              type='textarea'
              name='footnote'
              value={form.footnote}
              onChange={e => set('footnote', e.target.value)}
            />
          </MDBCol>
          <MDBCol size='12' sm='4'>
            <UIInput
              label='Sort Order'
              type='number'
              name='sort_order'
              value={form.sort_order}
              onChange={e => set('sort_order', e.target.value)}
            />
          </MDBCol>
        </MDBRow>

        <fieldset className='product-states'>
          <legend>
            Available States <span className='selected-count'>({selected.length} selected)</span>
          </legend>
          <div className='state-actions'>
            <MDBBtn type='button' color='link' size='sm' onClick={() => setAllStates(true)}>
              Select all
            </MDBBtn>
            <MDBBtn type='button' color='link' size='sm' onClick={() => setAllStates(false)}>
              Clear
            </MDBBtn>
          </div>
          <div className='state-grid'>
            {STATE_CODES.map(code => (
              <label key={code} title={STATE_NAMES[code]}>
                <input type='checkbox' checked={form.states.hasOwnProperty(code)} onChange={() => toggleState(code)} />
                {code}
              </label>
            ))}
          </div>
        </fieldset>

        {selected.length ? (
          <details className='product-state-notes'>
            <summary>State notes (optional)</summary>
            <p className='state-notes-hint'>
              Shown on the map as &ldquo;{form.short_name || 'Short Label'}: note&rdquo;.
            </p>
            {selected.map(code => (
              <div className='state-note' key={code}>
                <label htmlFor={`product-state-note-${code}`}>{code}</label>
                <input
                  id={`product-state-note-${code}`}
                  type='text'
                  className='form-control form-control-sm'
                  placeholder={`Note for ${STATE_NAMES[code]}`}
                  value={form.states[code]}
                  onChange={e => setNote(code, e.target.value)}
                />
              </div>
            ))}
          </details>
        ) : null}

        <MDBRow>
          <MDBCol size='12' sm='7'>
            <MDBBtn
              color='default'
              className='btn-block'
              type='submit'
              disabled={CarrierMgmtStore.getIsLoading('update.product')}
            >
              {CarrierMgmtStore.getIsLoading('update.product') ? (
                <span>
                  Saving...&nbsp;
                  <i className='fa fa-spin fa-spinner' />
                </span>
              ) : (
                'Save Product'
              )}
            </MDBBtn>
          </MDBCol>
          <MDBCol size='12' sm='5'>
            <MDBBtn
              type='button'
              color='default'
              className='btn-block'
              onClick={() => {
                setForm(null)
                setError(null)
              }}
            >
              Cancel
            </MDBBtn>
          </MDBCol>
        </MDBRow>
      </form>
    )
  }

  const renderTable = () => {
    const products = CarrierMgmtStore.products.slice().sort(sortProducts),
      isLoading = CarrierMgmtStore.getIsLoading('fetch.products') || CarrierMgmtStore.getIsLoading('fetch.carrier')

    return (
      <>
        {renderError()}
        <table className={`carriers-table ${isScoped ? '' : 'with-carrier'}`}>
          <thead>
            <tr>
              {isScoped ? null : (
                <th valign='middle' className='carrier-name'>
                  Carrier
                </th>
              )}
              <th valign='middle'>Product</th>
              <th valign='middle'>States</th>
              <th valign='middle'>Edit</th>
              <th valign='middle'>Delete</th>
            </tr>
          </thead>
          <tbody>
            {products.length ? (
              products.map(product => (
                <tr key={'carrier-product-' + product.id}>
                  {isScoped ? null : <td className='carrier-name'>{product.carrier_name}</td>}
                  <td>
                    {product.name}
                    {isScoped ? null : <div className='product-carrier'>{product.carrier_name}</div>}
                  </td>
                  <td>{formatStateCount(product.states)}</td>
                  <td>
                    <MDBBtn
                      type='button'
                      color='default'
                      aria-label={`Edit ${product.name}`}
                      onClick={() => startEdit(product)}
                    >
                      <MDBIcon icon='edit' />
                    </MDBBtn>
                  </td>
                  <td>
                    <MDBBtn
                      type='button'
                      color='default'
                      aria-label={`Delete ${product.name}`}
                      onClick={() => onDelete(product)}
                    >
                      <MDBIcon icon='trash' className='red-text' />
                    </MDBBtn>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={isScoped ? 4 : 5} className='empty-row'>
                  {isLoading ? 'Loading products...' : CarrierMgmtStore.productsError || 'No products added yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </>
    )
  }

  return (
    <MDBCard id='CarrierProductsFormComponent' className={isScoped ? '' : 'all-carriers'}>
      {renderLockOut()}
      <MDBCardHeader>
        Product Availability&nbsp;
        {CarrierMgmtStore.getIsLoading('fetch.products') ? <i className='fa fa-spin fa-spinner' /> : null}
        <MDBBtn
          type='button'
          color='default'
          className='card-header-btn'
          aria-label='Add Product'
          title='Add Product'
          disabled={!!form || (!isScoped && !!carrierId)}
          onClick={() => startEdit(null)}
        >
          <MDBIcon icon='plus' />
        </MDBBtn>
      </MDBCardHeader>
      <MDBCardBody>{form ? renderForm() : renderTable()}</MDBCardBody>
    </MDBCard>
  )
}

export default observer(CarrierProductsForm)
